import { Injectable } from '@angular/core';
import { uniqueId } from 'lodash-es';
import { delay, map, Observable, of } from 'rxjs';

import { JobInfo, JobType } from '@blaze-shared/api-contracts-domain';
import { isNullish } from '@blaze-shared/shared-util';

import { GetJobsRequest, JobsApi } from './models/jobs-api';

interface MockedJobConfig {
  duration: number;
  doneData: Record<string, any>;
  projectId: string;
  type: JobType;
}

interface MockedJob {
  data: JobInfo<any>;
  cancel: () => void;
}

type MockedJobInfo = JobInfo<Record<string, any>> | null;

@Injectable({
  providedIn: 'root',
})
export class FakeJobsApiService implements JobsApi {
  private readonly jobsInProgress = new Map<string, MockedJob>();

  getJobs<T>(_request: GetJobsRequest): Observable<JobInfo<T>[]> {
    throw new Error('Method not implemented.');
  }

  postJobsStatus<T>(jobIds: string[] | string | null | undefined): Observable<JobInfo<T>[]> {
    if (isNullish(jobIds)) {
      jobIds = [];
    }

    if (typeof jobIds === 'string') {
      jobIds = [jobIds];
    }

    const randomArray = new Uint32Array(1);
    crypto.getRandomValues(new Uint32Array(1));
    const randomDelay = (randomArray[0] % 1501) + 500;

    return of(this.jobsInProgress.get(jobIds[0])).pipe(
      delay(randomDelay),
      map((job) => {
        if (!job) {
          throw new Error('Job not found');
        }
        return [job.data];
      }),
    );
  }

  postCancelJob(jobId: string): Observable<unknown> {
    const job = this.jobsInProgress.get(jobId);

    if (!job) {
      throw new Error('Job not found');
    }

    job.cancel();
    return of(job.data);
  }

  create(config: Partial<MockedJobConfig>): Observable<MockedJobInfo> {
    const _config: MockedJobConfig = {
      duration: 30000,
      doneData: { message: 'Done' },
      projectId: 'someProjectId',
      type: 'Workflow',
      ...config,
    };

    return new Observable((observer) => {
      const id = uniqueId('mocked-job-');

      const delayedActionTimeout = setTimeout(() => {
        const job = this.jobsInProgress.get(id);
        if (!job) {
          throw new Error('Job not found');
        }

        job.data.status = 'RanToCompletion';
        job.data.result = _config.doneData;
      }, _config.duration);

      const cancel = (): void => {
        const runningJob = this.jobsInProgress.get(id);
        clearTimeout(delayedActionTimeout);
        if (runningJob) {
          runningJob.data.status = 'Canceled';
        }
      };

      const newJob: MockedJobInfo = {
        accountId: 'someAccountId',
        affectedDatasetId: null,
        createdByUserId: 'someUserId',
        projectId: _config.projectId,
        type: _config.type,
        workflowId: null,
        id,
        status: 'Running',
        result: {},
        progress: null,
      };

      this.jobsInProgress.set(id, { data: newJob, cancel });
      observer.next({ ...newJob });
    });
  }
}
